import React, { useEffect, useState } from "react";
import { Radio, Search, RotateCcw, ChevronLeft, ChevronRight, Globe, Anchor } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface UdpListener {
    id: number;
    local_address: string;
    local_port: number;
}

const UdpListeners: React.FC = () => {
    const [data, setData] = useState<UdpListener[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { asset_id: asset_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { local_address: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
                if (!isNaN(parseInt(debouncedSearchTerm))) {
                    queryObj["$or"].push({ local_port: parseInt(debouncedSearchTerm) });
                }
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            const response = await baseapi.get("/udp_listeners", { params });
            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, debouncedSearchTerm]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);



    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header Area */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Radio className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-white leading-tight">UDP Listeners</h2>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search IP or Port..."
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => fetchData()}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                    >
                        <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Content Area - Cards Grid (5 per row) */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <RotateCcw className="w-10 h-10 text-orange-500 animate-spin" />
                    <span className="text-gray-400 font-medium">Loading UDP listeners...</span>
                </div>
            ) : data.length === 0 ? (
                <div className="bg-gray-800/50 rounded-xl border border-gray-700 border-dashed py-20 text-center">
                    <Radio className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No listeners found</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                    {data.map((listener) => (
                        <div
                            key={listener.id}
                            className="bg-gray-800 border border-gray-700 rounded-xl p-3 hover:border-orange-500/50 transition-all group shadow-lg flex flex-col justify-between"
                        >
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <Globe className="w-3.5 h-3.5 text-orange-500" />
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate font-mono">
                                        {listener.local_address}
                                    </span>
                                </div>
                                <span className="bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded text-[9px] font-bold border border-blue-500/20">
                                    UDP
                                </span>
                            </div>

                            <div className="flex items-center gap-2 mt-auto">
                                <Anchor className="w-4 h-4 text-gray-400" />
                                <div className="text-lg font-bold text-white leading-tight">
                                    {listener.local_port}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Compact Pagination Footer */}
            {!loading && totalItems > 0 && (
                <div className="bg-gray-900/40 rounded-xl px-4 py-2 flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">Show</span>
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={(val) => {
                                    setItemsPerPage(val);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                        <span className="text-xs text-gray-400">
                            Total: {totalItems} listeners
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            <ChevronLeft className="w-4 h-4 -ml-2" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-1 mx-2">
                            <span className="bg-orange-600/20 text-orange-500 w-7 h-7 flex items-center justify-center rounded text-xs font-bold border border-orange-500/30">
                                {currentPage}
                            </span>
                            <span className="text-gray-500 text-xs px-1">/</span>
                            <span className="text-gray-400 text-xs font-bold">{totalPages}</span>
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                            <ChevronRight className="w-4 h-4 -ml-2" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UdpListeners;
